import { getIsDebug } from '@ui/constants/isDebug';
import {
  PAIR_AMOUNT_DECIMALS,
  USD_DECIMALS,
} from '@ui/constants/numbersDecimals';
import { getPayablePoolTokenByAddress } from '@ui/constants/tokens';
import { toReadableValue } from '@ui/lib/bignumber';
import { tryReportError } from '@ui/lib/bugsnag';
import {
  ClosePositionDetailExecutionTypeEnum,
  PagedTradeHistoryResponse,
  TradeHistoryResponseOperationTypeEnum,
} from '@ui/services/backend/generated';
import { ChainId } from '@ui/types/chain';
import { BareTradePair } from '@ui/types/tradePair';
import { getExplorerLink } from '@ui/utils/links';
import { getLeverage } from '@ui/utils/trade/getLeverage';
import { getOpenPositionFee } from '@ui/utils/trade/getOpenPositionFee';
import { getPositionPnlAfterFee } from '@ui/utils/trade/getPositionPnlAfterFee';
import BigNumber from 'bignumber.js';
import { isNil } from 'lodash-es';
import { Address } from 'viem';

import { ExtendedTradeHistoryResponse } from './serverType';
import {
  DecreaseOrderKind,
  ExecuteCloseSuccessful,
  HistoryStatus,
  TradeHistory,
  TradeHistoryResponse,
} from './type';
import { getIsHiddenEvent } from './util';

const toDecreaseOrderKind = (kind?: number | string): DecreaseOrderKind => {
  if (typeof kind === 'string') {
    const normalizedKindName = kind.replace(/[\s_-]/g, '').toLowerCase();

    if (normalizedKindName === 'tp' || normalizedKindName === 'takeprofit') {
      return 'TP';
    }
    if (normalizedKindName === 'sl' || normalizedKindName === 'stoploss') {
      return 'SL';
    }
  }

  const normalizedKind = kind === undefined ? undefined : Number(kind);

  if (normalizedKind === 0) return 'TP';
  if (normalizedKind === 1) return 'SL';

  return 'Order';
};

const toOptionalReadableValue = (
  value: string | number | undefined,
  decimals: number,
) => {
  if (isNil(value) || value === '') return undefined;

  return toReadableValue(BigInt(value), decimals);
};

const toOptionalNumber = (value: string | number | undefined) => {
  if (isNil(value) || value === '') return undefined;

  return Number(value);
};

export const toTradeHistoryResponse = (
  backendData: PagedTradeHistoryResponse,
  page: number,
  size: number,
  chainId: ChainId,
  pairs: BareTradePair[],
): TradeHistoryResponse => {
  const histories = (backendData.content ?? [])
    .map((history): TradeHistory | null => {
      const pair = pairs.find(
        (p) => p.address.toLowerCase() === history.pairBase.toLowerCase(),
      );
      if (!pair) {
        tryReportError(
          new Error(
            `pairBase: ${history.pairBase} is not found in listing config, chainId: ${chainId}`,
          ),
        );

        return null;
      }

      return toTradeHistory(
        history as ExtendedTradeHistoryResponse,
        chainId,
        pair,
      );
    })
    .filter((v): v is TradeHistory => {
      const isNonNull = !!v;
      if (getIsDebug()) {
        // show all hidden event in debug mode
        return isNonNull;
      }

      return isNonNull && !getIsHiddenEvent(v);
    });

  return {
    page: page,
    size: size,
    totalPages: backendData.totalPages,
    histories,
  };
};

const toTradeHistory = (
  history: ExtendedTradeHistoryResponse,
  chainId: ChainId,
  pair: BareTradePair,
): TradeHistory | null => {
  const lvToken = getPayablePoolTokenByAddress(
    history.lvToken as Address,
    chainId,
  );
  const tokenIn = getPayablePoolTokenByAddress(
    history.tokenIn as Address,
    chainId,
  );

  if (!lvToken || !tokenIn) {
    // if not found, possibly the pair is unlisted, or event created through contract directly
    console.error(
      'lvToken: ' + history.lvToken + ' is not listed in client config',
    );

    return null;
  }

  if (!history.operationType) {
    console.error('missing operation type');

    return null;
  }

  const isLVTokenPay =
    tokenIn?.address.toLowerCase() === lvToken.address.toLowerCase();
  const size = toReadableValue(BigInt(history.qty), PAIR_AMOUNT_DECIMALS);
  const tokenInPrice = history.tokenInPrice
    ? new BigNumber(history.tokenInPrice) // BE already normalized decimals
    : undefined;
  const amountIn = toReadableValue(BigInt(history.amountIn), tokenIn.decimals);
  const amountInUsd = tokenInPrice ? amountIn.times(tokenInPrice) : undefined;
  const collateral = history.margin
    ? toReadableValue(BigInt(history.margin), lvToken.decimals)
    : undefined;
  const entryPrice = history.position?.entryPrice
    ? toReadableValue(BigInt(history.position.entryPrice), USD_DECIMALS)
    : undefined;
  const groupHash = history.position
    ? [
        history.position.positionHash,
        history.position.entryPrice,
        history.position.marginToken,
        history.position.isLong ? 'long' : 'short',
      ].join(':')
    : null;

  // FIXME: BE will provide price snapshot both pair and collateral
  const _getSizeUsd: TradeHistory['getSizeUsd'] = (pairPrice) => {
    if (!pairPrice) return;

    return size.times(pairPrice);
  };
  // FIXME: BE will provide price snapshot both pair and collateral
  const _getCollateralUsd: TradeHistory['getCollateralUsd'] = ({
    pairPrice,
    collateralPrice,
    openFeeP,
    lvTokenDiscountP,
    executionFeeUsd,
  }) => {
    if (
      !pairPrice ||
      isNil(lvTokenDiscountP) ||
      isNil(openFeeP) ||
      !executionFeeUsd
    )
      return;

    const _collateralPrice = tokenInPrice ?? collateralPrice;
    if (!_collateralPrice) return;
    if (collateral) {
      return collateral.times(_collateralPrice);
    } else {
      const sizeUsd = _getSizeUsd(pairPrice);
      if (!sizeUsd) return;

      return amountIn
        .times(_collateralPrice)
        .minus(executionFeeUsd)
        .minus(
          getOpenPositionFee({
            openFeeP,
            lvTokenDiscountP,
            isPayLVToken: isLVTokenPay,
            positionSize: sizeUsd,
          }).final,
        );
    }
  };
  // FIXME: BE will provide price snapshot both pair and collateral
  const _getLeverage: TradeHistory['getLeverage'] = ({
    collateralPrice,
    executionFeeUsd,
    openFeeP,
    lvTokenDiscountP,
    pairPrice,
  }) => {
    const leveragePairPrice = entryPrice ?? pairPrice;
    const collateral = _getCollateralUsd({
      collateralPrice,
      executionFeeUsd,
      openFeeP,
      lvTokenDiscountP,
      pairPrice: leveragePairPrice,
    });
    const size = _getSizeUsd(leveragePairPrice);
    if (!collateral || !size) return;

    return getLeverage({
      collateralUsd: collateral,
      sizeUsd: size,
    });
  };

  const common = {
    explorer: getExplorerLink(history.transactionHash, 'transaction', chainId),
    blockTime: new Date(history.blockTime),
    blockNumber: history.blockNumber,
    isLong: history.isLong,
    tradeType: history.tradeType,
    pair,
    size,
    entryPrice,
    // Visual grouping only. Merged positions can reuse positionHash across
    // lifecycles, so include stable position snapshot fields until BE exposes
    // an explicit lifecycle id / open tx hash / sequence.
    groupHash,
    getSizeUsd: _getSizeUsd,
    getLeverage: _getLeverage,
    id: history.id,
    transactionHash: history.transactionHash,
    transactionIndex: history.transactionIndex,
    logIndex: history.logIndex,
    lvToken: lvToken,
    tokenIn: tokenIn,
    tokenInPrice,
    amountInUsd,
    amountIn: amountIn,
    collateral,
    getCollateralUsd: _getCollateralUsd,
  } satisfies Omit<TradeHistory, 'status' | 'detail' | 'type'>;

  switch (history.operationType) {
    case TradeHistoryResponseOperationTypeEnum.OpenLimitOrder:
      const price = toReadableValue(BigInt(history.detail.price), USD_DECIMALS);

      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.OpenLimitOrder,
        status: HistoryStatus.CREATED,
        getLeverage(params) {
          return _getLeverage({
            ...params,
            pairPrice: price,
          });
        },
        detail: {
          amountIn: toReadableValue(
            BigInt(history.detail.amountIn),
            tokenIn.decimals,
          ),
          price,
          stopLoss: toReadableValue(
            BigInt(history.detail.stopLoss),
            USD_DECIMALS,
          ),
          takeProfit: toReadableValue(
            BigInt(history.detail.takeProfit),
            USD_DECIMALS,
          ),
        },
      };
    case TradeHistoryResponseOperationTypeEnum.UpdateOrderSl:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.UpdateOrderSl,
        status: HistoryStatus.UPDATED,

        detail: {
          oldSl: toReadableValue(BigInt(history.detail.oldSl), USD_DECIMALS),
          sl: toReadableValue(BigInt(history.detail.sl), USD_DECIMALS),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.UpdateOrderTp:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.UpdateOrderTp,
        status: HistoryStatus.UPDATED,

        detail: {
          oldTp: toReadableValue(BigInt(history.detail.oldTp), USD_DECIMALS),
          tp: toReadableValue(BigInt(history.detail.tp), USD_DECIMALS),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.UpdateMargin:
      if (!collateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      return {
        ...common,
        collateral,
        type: TradeHistoryResponseOperationTypeEnum.UpdateMargin,
        status: HistoryStatus.UPDATED,
        detail: {
          previousCollateral: toReadableValue(
            BigInt(history.detail.beforeMargin),
            tokenIn.decimals,
          ),
          nextCollateral: toReadableValue(
            BigInt(history.detail.margin),
            tokenIn.decimals,
          ),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.UpdateTradeTp:
      if (!collateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      return {
        ...common,
        collateral,
        type: TradeHistoryResponseOperationTypeEnum.UpdateTradeTp,
        status: HistoryStatus.UPDATED,

        detail: {
          oldTp: toReadableValue(BigInt(history.detail.oldTp), USD_DECIMALS),
          tp: toReadableValue(BigInt(history.detail.tp), USD_DECIMALS),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.UpdateTradeSl:
      if (!collateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      return {
        ...common,
        collateral,
        type: TradeHistoryResponseOperationTypeEnum.UpdateTradeSl,
        status: HistoryStatus.UPDATED,
        detail: {
          oldSl: toReadableValue(BigInt(history.detail.oldSl), USD_DECIMALS),
          sl: toReadableValue(BigInt(history.detail.sl), USD_DECIMALS),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.PendingTradeRefund:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.PendingTradeRefund,
        status: HistoryStatus.CANCELLED,
        detail: {
          refund: history.detail.refund,
        },
      };

    case TradeHistoryResponseOperationTypeEnum.MarketPendingTrade: {
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.MarketPendingTrade,
        status: HistoryStatus.CREATED,
        detail: {
          amountIn: toReadableValue(BigInt(history.detail.amountIn)),
          price: toReadableValue(BigInt(history.detail.price), USD_DECIMALS),
          stopLoss: toReadableValue(
            BigInt(history.detail.stopLoss),
            USD_DECIMALS,
          ),
          takeProfit: toReadableValue(
            BigInt(history.detail.takeProfit),
            USD_DECIMALS,
          ),
          broker: history.detail.broker,
        },
      };
    }

    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderCreated,
        status: HistoryStatus.CREATED,
        detail: {
          positionHash: history.detail.positionHash,
          orderHash: history.detail.orderHash,
          kind: toDecreaseOrderKind(history.detail.kind),
          triggerPrice: toOptionalReadableValue(
            history.detail.triggerPrice,
            USD_DECIMALS,
          ),
          closeQty: toOptionalReadableValue(
            history.detail.closeQty,
            PAIR_AMOUNT_DECIMALS,
          ),
          broker: toOptionalNumber(history.detail.broker),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderCancelled:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderCancelled,
        status: HistoryStatus.CANCELLED,
        detail: {
          positionHash: history.detail?.positionHash,
          orderHash: history.detail?.orderHash,
          kind: toDecreaseOrderKind(history.detail?.kind),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.DecreaseOrderUpdated,
        status: HistoryStatus.UPDATED,
        detail: {
          positionHash: history.detail.positionHash,
          orderHash: history.detail.orderHash,
          kind: toDecreaseOrderKind(history.detail.kind),
          oldKind: toDecreaseOrderKind(
            history.detail.oldKind ?? history.detail.kind,
          ),
          oldTriggerPrice: toOptionalReadableValue(
            history.detail.oldTriggerPrice ??
              history.detail.beforeTriggerPrice ??
              history.detail.previousTriggerPrice,
            USD_DECIMALS,
          ),
          triggerPrice: toOptionalReadableValue(
            history.detail.triggerPrice,
            USD_DECIMALS,
          ),
          oldCloseQty: toOptionalReadableValue(
            history.detail.oldCloseQty ??
              history.detail.beforeCloseQty ??
              history.detail.previousCloseQty,
            PAIR_AMOUNT_DECIMALS,
          ),
          closeQty: toOptionalReadableValue(
            history.detail.closeQty,
            PAIR_AMOUNT_DECIMALS,
          ),
          broker: toOptionalNumber(history.detail.broker),
        },
      };

    case TradeHistoryResponseOperationTypeEnum.ExecuteCloseRejected:
      if (!collateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      return {
        ...common,
        collateral,
        type: TradeHistoryResponseOperationTypeEnum.ExecuteCloseRejected,
        status: HistoryStatus.CANCELLED,
        detail: null,
      };

    case TradeHistoryResponseOperationTypeEnum.ExecuteCloseSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ExecuteDecreaseOrderSuccessful:
    case TradeHistoryResponseOperationTypeEnum.ClosePosition:
    case TradeHistoryResponseOperationTypeEnum.PositionDecreased:
    case TradeHistoryResponseOperationTypeEnum.CloseTradeSuccessful: {
      if (!collateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      // after multiplied -1, funding fee > 0 is paying, < 0 is earning, align with holding fee
      const fundingFee = toReadableValue(
        BigInt(history.detail.fundingFee),
        lvToken.decimals,
      ).multipliedBy(-1);
      const closeFee = toReadableValue(
        BigInt(history.detail.closeFee),
        lvToken.decimals,
      );
      const holdingFee = toReadableValue(
        BigInt(history.detail.holdingFee),
        lvToken.decimals,
      );
      let pnl = toReadableValue(BigInt(history.detail.pnl), lvToken.decimals);
      const closePrice = toReadableValue(
        BigInt(history.detail.closePrice),
        USD_DECIMALS,
      );

      let pnlAfterFee = getPositionPnlAfterFee({
        pnl,
        closeFee: {
          closeFee: closeFee,
          finalCloseFee: closeFee,
        },
        fundingFee,
        holdingFee,
      });

      if (
        history.detail.executionType ===
        ClosePositionDetailExecutionTypeEnum.Liq
      ) {
        pnlAfterFee = collateral.negated();
        pnl = pnlAfterFee.plus(fundingFee).plus(holdingFee).plus(closeFee);
      }

      const _getPnlUsd: ExecuteCloseSuccessful['detail']['getPnlUsd'] = (
        collateralTokenPrice,
      ) => {
        const closeCollateralPrice = tokenInPrice || collateralTokenPrice;
        if (closeCollateralPrice) {
          return pnl.times(closeCollateralPrice);
        }
      };

      return {
        ...common,
        collateral,

        type: history.operationType,
        status: HistoryStatus.EXECUTED,
        detail: {
          executionType: history.detail.executionType,
          closePrice,
          fundingFee,
          closeFee,
          holdingFee,
          pnl,
          getPnlUsd: _getPnlUsd,
          pnlAfterFee,
          getPnlAfterFeeUsd: (collateralTokenPrice) => {
            const price = tokenInPrice ?? collateralTokenPrice;
            if (!price) return;

            return pnlAfterFee.times(price);
          },
        },
      };
    }

    case TradeHistoryResponseOperationTypeEnum.CancelLimitOrder:
    case TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderRejected:
    case TradeHistoryResponseOperationTypeEnum.ExecuteLimitOrderSuccessful:
      return {
        ...common,
        type: history.operationType,
        status: HistoryStatus.EXECUTED,
        detail: null,
      };
    case TradeHistoryResponseOperationTypeEnum.LimitOrderRefund:
      return {
        ...common,
        type: TradeHistoryResponseOperationTypeEnum.LimitOrderRefund,
        status: HistoryStatus.CANCELLED,
        detail: {
          refund: history.detail.refund,
        },
      };
    case TradeHistoryResponseOperationTypeEnum.OpenMarketTrade:
    case TradeHistoryResponseOperationTypeEnum.OpenPosition:
    case TradeHistoryResponseOperationTypeEnum.PositionIncreased:
      const detailCollateral = toReadableValue(
        BigInt(history.detail.collateral),
        tokenIn.decimals,
      );
      const eventCollateral =
        history.operationType ===
        TradeHistoryResponseOperationTypeEnum.PositionIncreased
          ? detailCollateral
          : collateral;

      if (!eventCollateral) {
        throw new Error(
          `type: ${history.operationType} should have collateral data`,
        );
      }

      return {
        ...common,
        collateral: eventCollateral,
        type: history.operationType,
        status: HistoryStatus.EXECUTED,
        detail: {
          atPrice: toReadableValue(
            BigInt(history.detail.atPrice),
            USD_DECIMALS,
          ),
          collateral: detailCollateral,
          executionFee: toReadableValue(
            BigInt(history.detail.executionFee),
            tokenIn.decimals,
          ),
          positionSize: toReadableValue(
            BigInt(history.detail.positionSize),
            PAIR_AMOUNT_DECIMALS,
          ),
        },
      };
  }
};
